import { useEffect, useState } from "react";

const STORAGE_KEY = "mb-theme";
// CoreUI's color-mode storage key (matches useColorModes(...) in AppSidebar).
const COREUI_KEY = "coreui-free-react-admin-template-theme";

// Apply a theme everywhere so the WHOLE app flips, not just our Tailwind tokens:
//  - `dark` class on <html>  → our glass tokens in styles/tailwind.css
//  - `data-coreui-theme`     → CoreUI/Bootstrap nested components (tables, cards,
//    inputs) — without this they stay light inside a dark layout
//  - both localStorage keys  → keeps CoreUI's useColorModes in sync so it doesn't
//    revert the attribute on mount
const applyTheme = (theme: "light" | "dark") => {
  const root = document.documentElement;
  root.classList.toggle("dark", theme === "dark");
  root.setAttribute("data-coreui-theme", theme);
  // Sync the browser chrome (mobile address/status bar) to the resolved --c-bg.
  const bg = getComputedStyle(root).getPropertyValue("--c-bg").trim();
  if (bg) {
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "theme-color";
      document.head.appendChild(meta);
    }
    meta.content = bg;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
    window.localStorage.setItem(COREUI_KEY, theme);
  } catch {
    /* ignore */
  }
};

// Shared light/dark state + toggle. Reads the persisted choice on mount (default
// dark, matching _app's initial application) and applies it everywhere. Used by
// any control that flips the theme (the user-menu item, etc.).
export const useTheme = () => {
  const [theme, setTheme] = useState<"light" | "dark">("dark");

  useEffect(() => {
    let initial: "light" | "dark" = "dark";
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored === "light" || stored === "dark") initial = stored;
    } catch {
      /* ignore */
    }
    setTheme(initial);
    applyTheme(initial);
  }, []);

  const toggle = () => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    applyTheme(next);
  };

  return { theme, isDark: theme === "dark", toggle };
};
